/**
 * Broker Invoice — field-level effects.
 *
 * Migrated from: broker/broker-invoice-base.coffee
 *
 * Old validators → new effects:
 *   CopyClaimLineBroker        → CopyClaimLineBroker
 *   CalculateClaimAmountBroker → CalculateClaimAmountBroker
 *
 * Skipped:
 *   handle(dfd) init logic — hides subform buttons, handled by React natively
 *   open_lineitem / open_adjustment — Backbone flyout events, handled by React natively
 *   delete_row — DOM event for recalculation, handled by CalculateClaimAmountBroker
 */
import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

function getRelationID(value: any): number | undefined {
	if (value === null || value === undefined || value === "") {
		return undefined;
	}
	if (typeof value === "number") {
		return Number.isNaN(value) ? undefined : value;
	}
	if (typeof value === "string") {
		const parsed = parseInt(value, 10);
		return Number.isNaN(parsed) ? undefined : parsed;
	}
	if (typeof value === "object" && "id" in value) {
		return getRelationID(value.id);
	}
	return undefined;
}

// ─── CalculateClaimAmountBroker ────────────────────────────────────────────
// Old: DSLFx.Validators.CalculateClaimAmountBroker → handler.calculate_claim_amount
// Trigger: called internally after CopyClaimLineBroker populates sub-tables.
// Sums commission/funding/amount across lineitem and adjustment sub-tables,
// writes totals to amount, amount_outstanding_new, amount_outstanding,
// funding_amount, adjustment_amount.
export const CalculateClaimAmountBroker: FieldHandler = (field, { form }) => {
	let amt = 0;
	let fundingAmt = 0;
	let adjustmentAmt = 0;
	let amountNew = 0;
	let amountOutstanding = 0;

	const subTables: Record<string, string> = {
		lineitem_paid: "lineitem_paid",
		lineitem_outstanding: "lineitem_outstanding",
		lineitem_current: "lineitem_current",
		adjustment: "adjustment",
	};

	for (const [key, fieldName] of Object.entries(subTables)) {
		const vals: any[] = form.getFieldState(fieldName)?.value || [];
		for (const val of vals) {
			if (!val?.commission && !val?.amount) continue;

			if (key === "lineitem_paid") {
				amt += parseFloat(val.commission) || 0;
				fundingAmt += parseFloat(val.funding) || 0;
			} else if (key === "lineitem_outstanding") {
				amountOutstanding += parseFloat(val.commission) || 0;
			} else if (key === "lineitem_current") {
				amountNew += parseFloat(val.commission) || 0;
			} else {
				amt += parseFloat(val.amount) || 0;
				adjustmentAmt += parseFloat(val.amount) || 0;
			}
		}
	}

	form.setFieldState("amount", (s) => {
		s.value = amt;
	});
	form.setFieldState("amount_outstanding_new", (s) => {
		s.value = amountNew;
	});
	form.setFieldState("amount_outstanding", (s) => {
		s.value = amountOutstanding;
	});
	try {
		form.setFieldState("funding_amount", (s) => {
			s.value = fundingAmt;
		});
	} catch {
		// funding_amount may not exist in definition
	}
	form.setFieldState("adjustment_amount", (s) => {
		s.value = adjustmentAmt;
	});

	return "";
};

// ─── CopyClaimLineBroker ───────────────────────────────────────────────────
// Old: DSLFx.Validators.CopyClaimLineBroker → handler.copy_lineitem
// Trigger field: auto_lineitems (props.form="lineitem") / auto_adjustments (props.form="adjustment")
// Fetches unbilled lineitems or adjustments for the broker and populates
// the corresponding sub-table fields. Recalculates totals afterward.
export const CopyClaimLineBroker: FieldHandler = (
	field,
	{ form, editID },
	props
) => {
	void (async () => {
		const formType = (props?.form as string) || "lineitem";
		const triggerField =
			formType === "lineitem" ? "auto_lineitems" : "auto_adjustments";
		const value = form.getFieldState(triggerField)?.value;
		const brokerId = getRelationID(form.getFieldState("broker_id")?.value);
		const id = editID;

		if (!brokerId || value !== "Yes") return;

		setTimeout(() => {
			form.setFieldState(triggerField, (s) => {
				s.value = "No";
			});
		}, 250);

		try {
			const url =
				formType === "lineitem"
					? buildApiUrl(`/bi/broker/broker_lineitems/${brokerId}`)
					: buildApiUrl(
							`/broker_claim_adjustment/list?_invoice_id=eq:null&_broker_id=eq:${brokerId}&_auto_name=ex:true`
						);

			const data = await fetchAPI(url);
			const rows = Array.isArray(data) ? data : (data?.data ?? []);
			addBrokerDataRows(form, rows, formType);

			if (id && formType === "adjustment") {
				try {
					const data2 = await fetchAPI(
						buildApiUrl(
							`/broker_claim_adjustment/list?_invoice_id=eq:${id}&_broker_id=eq:${brokerId}&_auto_name=ex:true`
						)
					);
					const rows2 = Array.isArray(data2)
						? data2
						: (data2?.data ?? []);
					addBrokerDataRows(form, rows2, formType);
				} catch {
					// best-effort only
				}
			}

			recalcBrokerAmounts(form);
		} catch {
			// best-effort only
		}
	})();
	return "";
};

function addBrokerDataRows(form: any, data: any[], formType: string) {
	if (formType === "lineitem") {
		const paid: any[] = form.getFieldState("lineitem_paid")?.value || [];
		const outstanding: any[] =
			form.getFieldState("lineitem_outstanding")?.value || [];
		const current: any[] =
			form.getFieldState("lineitem_current")?.value || [];
		const existingIds = new Set(
			[...paid, ...outstanding, ...current].map((v) => parseInt(v._meta))
		);

		const newPaid = [...paid];
		const newOutstanding = [...outstanding];
		const newCurrent = [...current];

		for (const v of data) {
			if (existingIds.has(parseInt(v.id))) continue;

			const row = {
				_meta: parseInt(v.id),
				plan: v.plan_name,
				invoice_date: v.original_invoice_date,
				funding: parseFloat(v.funding_amount || 0).toFixed(2),
				commission: parseFloat(v.commission_paid || 0).toFixed(2),
			};

			if (v.outstanding === "Yes") newOutstanding.push(row);
			if (v.new_item === "Yes") newCurrent.push(row);
			if (v.payable === "Yes") newPaid.push(row);
		}

		form.setFieldState("lineitem_paid", (s: any) => {
			s.value = newPaid;
		});
		form.setFieldState("lineitem_outstanding", (s: any) => {
			s.value = newOutstanding;
		});
		form.setFieldState("lineitem_current", (s: any) => {
			s.value = newCurrent;
		});
	} else {
		const existing: any[] = form.getFieldState("adjustment")?.value || [];
		const existingIds = new Set(existing.map((v) => parseInt(v._meta)));
		const newAdj = [...existing];

		for (const v of data) {
			if (existingIds.has(parseInt(v.id))) continue;
			newAdj.push({
				_meta: parseInt(v.id),
				date: v.date,
				amount: parseFloat(v.amount || 0).toFixed(2),
				comment: v.comment,
			});
		}

		form.setFieldState("adjustment", (s: any) => {
			s.value = newAdj;
		});
	}
}

function recalcBrokerAmounts(form: any) {
	let amt = 0;
	let fundingAmt = 0;
	let adjustmentAmt = 0;
	let amountNew = 0;
	let amountOutstanding = 0;

	const tables: Record<string, string> = {
		lineitem_paid: "lineitem_paid",
		lineitem_outstanding: "lineitem_outstanding",
		lineitem_current: "lineitem_current",
		adjustment: "adjustment",
	};

	for (const [key, fieldName] of Object.entries(tables)) {
		const vals: any[] = form.getFieldState(fieldName)?.value || [];
		for (const val of vals) {
			if (!val?.commission && !val?.amount) continue;
			if (key === "lineitem_paid") {
				amt += parseFloat(val.commission) || 0;
				fundingAmt += parseFloat(val.funding) || 0;
			} else if (key === "lineitem_outstanding") {
				amountOutstanding += parseFloat(val.commission) || 0;
			} else if (key === "lineitem_current") {
				amountNew += parseFloat(val.commission) || 0;
			} else {
				amt += parseFloat(val.amount) || 0;
				adjustmentAmt += parseFloat(val.amount) || 0;
			}
		}
	}

	form.setFieldState("amount", (s: any) => {
		s.value = amt;
	});
	form.setFieldState("amount_outstanding_new", (s: any) => {
		s.value = amountNew;
	});
	form.setFieldState("amount_outstanding", (s: any) => {
		s.value = amountOutstanding;
	});
	try {
		form.setFieldState("funding_amount", (s: any) => {
			s.value = fundingAmt;
		});
	} catch {
		// funding_amount may not exist in definition
	}
	form.setFieldState("adjustment_amount", (s: any) => {
		s.value = adjustmentAmt;
	});
}
